import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Mail, MessageSquare, Phone, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { CommunicationError, Organisation, Pagination } from "./types";
import { axiosInstance } from "../../utility/apiInstance";
import { useRole } from "../MangeContextProvide";
import SearchInput from "../../common/SearchInput";


// ─── Helpers ──────────────────────────────────────────────────────────────────
const channelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
        case "email": return <Mail size={14} className="inline mr-1" />;
        case "sms": return <Phone size={14} className="inline mr-1" />;
        default: return <MessageSquare size={14} className="inline mr-1" />;
    }
};

const formatDate = (iso: string) => {
    const d = new Date(iso);
    return (
        d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) +
        " " +
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
};



// ─── Component ────────────────────────────────────────────────────────────────
export default function CommunicationErrorsTable() {
    const { organizationId, role } = useRole() as any;
    const isAdmin = role === "admin";

    // Data state
    const [errors, setErrors] = useState<CommunicationError[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Admin — organisation list + selection
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string>("");
    const [orgLoading, setOrgLoading] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // ── Fetch organisation list (admin only) ──────────────────────────────────
    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            setOrgLoading(true);
            try {
                const { data } = await axiosInstance("/api/v1/organisation");
                const list: Organisation[] = (data?.data?.data ?? data?.data ?? [])
                    .filter((ele: any) => ele.isActive == true)
                    .map((ele: any) => ({ _id: ele._id, name: ele.organizationName }));
                setOrganisations(list);
                setSelectedOrgId("");
            } catch {
                // silently fail
            } finally {
                setOrgLoading(false);
            }
        })();
    }, [isAdmin]);

    // ── Resolve which org ID to query ─────────────────────────────────────────
    const activeOrgId = isAdmin ? selectedOrgId : organizationId;

    // ── Fetch errors ──────────────────────────────────────────────────────────
    const fetchErrors = async (p = page, l = limit, search = debouncedSearch) => {
        setLoading(true);
        setFetchError(null);
        try {
            const searchParam = search.trim()
                ? `&search=${encodeURIComponent(search.trim())}`
                : "";
            const { data } = await axiosInstance(
                `/api/v1/communication-errors/${activeOrgId}?page=${p}&limit=${l}${searchParam}`
            );
            setErrors(data.data.data);
            setPagination(data.data.pagination);
        } catch {
            setFetchError("Unable to reach the server. Please check your connection.");
        } finally {
            setLoading(false);
        }
    };

    // ── Debounce search input ─────────────────────────────────────────────────
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    // ── Re-fetch on debounced search change (reset to page 1) ─────────────────
    useEffect(() => {
        setPage(1);
        fetchErrors(1, limit, debouncedSearch);
    }, [debouncedSearch]);

    // ── Re-fetch on org change (reset to page 1) ──────────────────────────────
    useEffect(() => {
        setPage(1);
        fetchErrors(1, limit, debouncedSearch);
    }, [activeOrgId]);

    // ── Re-fetch on page / limit change ──────────────────────────────────────
    useEffect(() => {
        fetchErrors(page, limit, debouncedSearch);
    }, [page, limit]);

    // ── Pagination helpers ────────────────────────────────────────────────────
    const totalPages = pagination?.totalPages ?? 1;
    const canPrev = page > 1;
    const canNext = page < totalPages;

    const handlePageChange = (p: number) => {
        setPage(p);
    };

    // Build page number array (max 5 visible)
    const pageNumbers = (() => {
        const delta = 2;
        const range: number[] = [];
        for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
            range.push(i);
        }
        return range;
    })();

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ fontFamily: "Inter, sans-serif", padding: "10px 0", background: "#f5f6fa", minHeight: "100vh" }}>

            {/* ── Header ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1a1a2e" }}>
                        Communication Errors Log
                    </h2>
                    {pagination && (
                        <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#6b7280" }}>
                            {pagination.total} error{pagination.total !== 1 ? "s" : ""} found
                        </p>
                    )}
                </div>

                <SearchInput
                    placeholder="Search Errors"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    width={400}
                />

                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    {/* Admin — Organisation Dropdown */}
                    {isAdmin && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {/* <Building2 size={16} color="#6b7280" /> */}
                            <select
                                value={selectedOrgId}
                                onChange={e => setSelectedOrgId(e.target.value)}
                                disabled={orgLoading}
                                style={{
                                    padding: "8px 12px", borderRadius: "8px", border: "1px solid #e5e7eb",
                                    fontSize: "13px", color: "#111827", background: "#fff",
                                    cursor: "pointer", outline: "none", minWidth: "180px"
                                }}
                            >
                                <option value="">Select Organisation.</option>
                                {organisations.map(org => (
                                    <option key={org._id} value={org._id}>{org.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Table Card ── */}
            <div className="whiteBox">
                <div style={{
                    background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    overflow: "hidden", display: "flex", flexDirection: "column", minHeight: "560px"
                }}>
                    {loading ? (
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flex: 1,
                            minHeight: "350px",
                            color: "#9ca3af"
                        }}>
                            <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "12px" }} />
                        </div>
                    ) : fetchError ? (
                        <div style={{ padding: "60px", textAlign: "center", color: "#ef4444", flex: 1 }}>
                            <AlertTriangle size={28} style={{ marginBottom: "12px" }} />
                            <p style={{ margin: 0 }}>{fetchError}</p>
                        </div>
                    ) : (
                        <div className="communicationErrorTable" style={{ overflowX: "auto", overflowY: "auto", width: "100%", flex: 1 }}>
                            <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                                <colgroup>
                                    <col style={{ width: "70px" }} />
                                    <col style={{ width: "160px" }} />
                                    <col style={{ width: "120px" }} />
                                    <col style={{ width: "360px" }} />
                                    <col style={{ width: "180px" }} />
                                    <col style={{ width: "150px" }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                        {["S No.", "Tracking Number", "Channel", "Error Message", "Organization", "Created At"].map(h => (
                                            <th key={h} style={{
                                                padding: "12px 16px", textAlign: "left", fontSize: "12px",
                                                fontWeight: 700, color: "#6b7280", textTransform: "capitalize",
                                                letterSpacing: "0.05em", whiteSpace: "nowrap"
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {errors.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: "48px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
                                                No communication errors found.
                                            </td>
                                        </tr>
                                    ) : errors.map((err, i) => {
                                        const cellBorder = { borderBottom: "1px solid rgba(224, 224, 224, 1)" };
                                        return (
                                            <tr key={err._id}>
                                                {/* Row # — accounts for current page offset */}
                                                <td style={{ padding: "14px 16px", fontSize: "13px", color: "#9ca3af", fontWeight: 600, ...cellBorder }}>
                                                    {(page - 1) * limit + i + 1}
                                                </td>
                                                {/* Tracking Number */}
                                                <td style={{ padding: "14px 16px", ...cellBorder, overflow: "hidden" }}>
                                                    <span style={{
                                                        fontFamily: "monospace", fontSize: "12px", background: "#f3f4f6",
                                                        padding: "3px 8px", borderRadius: "6px", color: "#374151",
                                                        display: "inline-block", maxWidth: "100%", overflow: "hidden",
                                                        textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle"
                                                    }}>
                                                        {err?.trackingNumber || ""}
                                                    </span>
                                                </td>
                                                {/* Channel */}
                                                <td style={{ padding: "14px 16px", ...cellBorder }}>
                                                    <span style={{
                                                        display: "inline-flex", alignItems: "center",
                                                        padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600,
                                                        background: "#eff6ff", color: "#2563eb", border: "1px solid #dce7ff", textTransform: "capitalize"
                                                    }}>
                                                        {channelIcon(err.channel)}{err.channel}
                                                    </span>
                                                </td>
                                                {/* Error Message — truncated, full text on hover */}
                                                <td
                                                    style={{ padding: "14px 16px", ...cellBorder, overflow: "hidden" }}
                                                    title={err?.error?.message || err?.error?.errorMessage || ""}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                                        <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
                                                        <span style={{
                                                            fontSize: "13px", color: "#111827", fontWeight: 500,
                                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                            display: "block", minWidth: 0
                                                        }}>
                                                            {err?.error?.message || err?.error?.errorMessage || ""}
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* Organization */}
                                                <td style={{ padding: "14px 16px", ...cellBorder, overflow: "hidden" }}>
                                                    <span style={{
                                                        padding: "5px 8px", borderRadius: "20px", fontSize: "12px",
                                                        background: "#fefce8", border: "1px solid #61351b4a", color: "#92400e", fontWeight: 600,
                                                        display: "inline-block", maxWidth: "100%", overflow: "hidden",
                                                        textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle"
                                                    }}>
                                                        {err?.organisation?.organizationName}
                                                    </span>
                                                </td>
                                                {/* Created At */}
                                                <td style={{ padding: "14px 16px", fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap", ...cellBorder }}>
                                                    {formatDate(err.createdAt)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Pagination Footer — pinned to bottom of card ── */}
                    {pagination && !loading && !fetchError && (
                        <div style={{
                            padding: "12px 16px", borderTop: "1px solid #f3f4f6",
                            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px",
                            flexShrink: 0, background: "#fff"
                        }}>
                            <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                                Showing {errors.length === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} records
                            </span>

                            {/* Page buttons */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                {/* Prev */}
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={!canPrev}
                                    style={{
                                        padding: "6px 10px", borderRadius: "6px", border: "1px solid #e5e7eb",
                                        background: canPrev ? "#fff" : "#f9fafb", cursor: canPrev ? "pointer" : "default",
                                        color: canPrev ? "#374151" : "#d1d5db", display: "flex", alignItems: "center"
                                    }}
                                >
                                    <ChevronLeft size={15} />
                                </button>

                                {/* First page if not in range */}
                                {pageNumbers[0] > 1 && (
                                    <>
                                        <PageBtn n={1} current={page} onClick={handlePageChange} />
                                        {pageNumbers[0] > 2 && <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>}
                                    </>
                                )}

                                {pageNumbers.map(n => (
                                    <PageBtn key={n} n={n} current={page} onClick={handlePageChange} />
                                ))}

                                {/* Last page if not in range */}
                                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                                    <>
                                        {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                                            <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>
                                        )}
                                        <PageBtn n={totalPages} current={page} onClick={handlePageChange} />
                                    </>
                                )}

                                {/* Next */}
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={!canNext}
                                    style={{
                                        padding: "6px 10px", borderRadius: "6px", border: "1px solid #e5e7eb",
                                        background: canNext ? "#fff" : "#f9fafb", cursor: canNext ? "pointer" : "default",
                                        color: canNext ? "#374151" : "#d1d5db", display: "flex", alignItems: "center"
                                    }}
                                >
                                    <ChevronRight size={15} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
}

// ─── Page Button Sub-component ────────────────────────────────────────────────
function PageBtn({ n, current, onClick }: { n: number; current: number; onClick: (n: number) => void }) {
    const active = n === current;
    return (
        <button
            onClick={() => onClick(n)}
            style={{
                padding: "6px 11px", borderRadius: "6px", fontSize: "13px", fontWeight: active ? 700 : 400,
                border: active ? "none" : "1px solid #e5e7eb",
                background: active ? "#1a1a2e" : "#fff",
                color: active ? "#fff" : "#374151",
                cursor: "pointer", minWidth: "32px"
            }}
        >
            {n}
        </button>
    );
}